import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import React, { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { createGlobalStyle } from 'styled-components';

import { useTheme } from '../ThemeContext';
import { Rounded } from '../theme/Rounded';
import { Spacing } from '../theme/Spacing';
import { Select, SelectProps, Tooltip } from '../utils/local-antd';
import { AtiraIcon } from './AtiraIcon';
import { Flex } from './Flex';
import { Text } from './Text';

const StyledSelectContainer = styled(Flex)<{
  height?: string;
  disabled?: boolean;
}>`
  flex-direction: column;
  gap: ${Spacing.s};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  label {
    color: ${({ theme }) => theme.fgPrimary};
  }

  .ant-select {
    height: 100%;
  }

  .ant-select-selector:hover {
    border: 1px solid ${({ theme }) => theme.inputBorder} !important;
  }

  .ant-select-selector {
    border: 1px solid ${({ theme }) => theme.inputBorder} !important;
    background-color: ${({ theme }) => theme.bgInput} !important;
    height: ${({ height }) => height || '2.3rem'} !important;
  }

  .ant-select-selection-item {
    color: ${({ theme }) => theme.fgInput} !important;
  }

  svg {
    fill: ${({ theme }) => theme.fgInput};
  }

  .ant-select-selection-placeholder {
    color: ${({ theme }) => theme.fgInput};
    opacity: 0.7;
  }

  .ant-select-clear {
    background-color: ${({ theme }) => theme.fgPrimary};
    border-radius: ${Rounded.circle};
  }
`;

const Required = styled.span`
  color: ${({ theme }) => theme.danger};
`;

const MenuThemedStyle = createGlobalStyle`
  .ant-select-dropdown {
    background-color: ${({ theme }) => theme.bgCanvas};
  }

  .ant-select-item-option-content { 
    color: ${({ theme }) => theme.fgPrimary};
    white-space: normal !important;
  }

  .ant-select-item-option-selected {
    background-color: ${({ theme }) => theme.fgInput}20 !important;
  }


`;

const OptionalSpan = styled.span`
  color: ${({ theme }) => theme.fgMuted};
  display: inline-block;
  margin-inline-start: ${Spacing.s};
`;

const StyledLabel = styled.label`
  width: max-content;
`;

// @ts-ignore
const StyledSelect = styled<{ dataTestId?: string }>(Select).attrs((props) => ({
  // @ts-ignore
  'data-testid': props.dataTestId,
}))``;

interface AtiraSelectProps extends SelectProps {
  width?: string;
  height?: string;
  borderRadius?: string;
  title?: string;
  info?: React.ReactNode | string;
  containerStyle?: CSSProperties;
  required?: boolean;
  valid?: boolean;
  errorMessage?: string;
  dataTestId?: string;
  hideOptionalMark?: boolean;
}

export const AtiraSelect: React.FC<AtiraSelectProps> = ({
  onChange,
  options,
  value,
  width,
  height,
  title,
  info,
  borderRadius,
  containerStyle,
  required = false,
  valid,
  errorMessage,
  dataTestId,
  hideOptionalMark = false,
  ...props
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  return (
    <StyledSelectContainer
      style={containerStyle}
      height={height}
      disabled={props.disabled}
    >
      {title ? (
        <StyledLabel>
          {info ? (
            <Tooltip title={info}>
              <AtiraIcon icon={faInfoCircle} color="fgPrimary" />
              {/* don't delete this span */}
              <span style={{ marginInlineEnd: '0.2rem' }}></span>
            </Tooltip>
          ) : null}

          {title}
          {required ? <Required> *</Required> : null}

          {!required && !hideOptionalMark ? (
            <OptionalSpan>({t('common.optional')})</OptionalSpan>
          ) : null}
        </StyledLabel>
      ) : null}

      <StyledSelect
        onChange={onChange}
        value={value}
        options={options}
        placeholder={props.placeholder || t('common.dropdown.select')}
        dataTestId={dataTestId}
        {...props}
      />
      {valid === false && errorMessage ? (
        <Text color="danger" fontSize="s">
          {errorMessage}
        </Text>
      ) : null}
      <MenuThemedStyle theme={theme} />
    </StyledSelectContainer>
  );
};
